import CartAPI from '../../api/cart-api'
import { fetchConfig } from '../../utils/fetch-config'
import Notification from '../common/notification'
import NotificationComponent from '../notification/notification'
import Sidebar from '../sidebar/sidebar'
import { getTemplateFirstChildContent, parseHTML } from '../../utils/dom'
import { debounce } from '../../utils/debounce'
import CartItem from './cart-item'
import { Nullable } from '../../types/common'
import {
  appendCustomElementToBody,
  removeCustomElementFromBody,
} from '../../utils/utils'

class CartDrawer extends Sidebar {
  viewport: Nullable<HTMLElement>
  results: Nullable<HTMLElement>
  header: Nullable<HTMLElement>
  footer: Nullable<HTMLElement>
  cartAPI: CartAPI
  resizeObserver: ResizeObserver

  constructor() {
    super()

    this.viewport = this.element.querySelector('[data-cart-drawer-viewport]')
    this.results = this.element.querySelector('[data-cart-drawer-results]')
    this.header = this.element.querySelector('[data-cart-drawer-header]')
    this.footer = this.element.querySelector('[data-cart-drawer-footer]')

    this.cartAPI = new CartAPI()

    if (this.results) {
      this.resizeObserver = new ResizeObserver(this.handleResultsResize)

      this.resizeObserver.observe(this.results)
    }
  }

  connectedCallback(): void {
    super.connectedCallback()

    this.addEventListener('change', this.handleCartChange)
    this.addEventListener('change', this.handleCartItemChange)

    if (this.viewport) {
      this.viewport.addEventListener('scroll', this.handleViewportScroll)
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback()

    this.removeEventListener('change', this.handleCartChange)
    this.removeEventListener('change', this.handleCartItemChange)

    if (this.viewport) {
      this.viewport.removeEventListener('scroll', this.handleViewportScroll)
    }

    this.resizeObserver.disconnect()
  }

  handleCartItemChange = (event): void => {
    const itemIndex = +event.target.dataset.index
    const cartItem = this.querySelector<CartItem>(`#CartItem-${itemIndex}`)

    if (!cartItem) {
      return
    }

    cartItem.setLoading(true)
  }

  handleCartChange = debounce((event): void => {
    const itemIndex = +event.target.dataset.index
    const value = +event.target.value

    this.updateItem(itemIndex, value)
  }, 500)

  handleViewportScroll = (): void => {
    this.updateViewport()
  }

  handleResultsResize = (): void => {
    this.updateViewport()
  }

  updateViewport(): void {
    if (!this.results || !this.viewport) {
      return
    }

    const isOverflow = this.results.offsetHeight > this.viewport.offsetHeight
    const isScrolled = this.viewport.scrollTop > 0

    this.classList.toggle('header-shadow-visible', isOverflow && isScrolled)
    this.classList.toggle('footer-shadow-visible', isOverflow)
  }

  updateItem(itemIndex: number, quantity: number): void {
    const config = this.createConfig(itemIndex, quantity)
    const cartItem = this.querySelector<CartItem>(`#CartItem-${itemIndex}`)

    if (!cartItem) {
      return
    }

    const currentQuantity = +(cartItem.dataset.quantity || 0)

    if (currentQuantity === quantity) {
      cartItem.setLoading(false)

      return
    }

    cartItem.setDisabled(true)

    if (quantity === 0) {
      cartItem.removeItem()
    }

    this.emit('start-update', {})

    this.cartAPI
      .change(config)
      .then(state => {
        const parsedState = JSON.parse(state)

        cartItem.setLoading(false)
        cartItem.setDisabled(false)

        const node = this.getTemplateContent(parsedState)

        this.mount()

        this.emit('update-nodes', { node })

        this.updateEmptyStatus()
      })
      .catch(() => {
        this.showError()
      })
  }

  showError(): void {
    const notificationElement = this.querySelector<NotificationComponent>(
      '#CartDrawerNotification',
    )

    if (notificationElement) {
      const notification = new Notification(notificationElement)

      notification.show(window.auroraThemeLocales.cartStrings.error, 'warning')
    }
  }

  purchaseHandler(parsedState: any): void {
    const node = this.getTemplateContent(parsedState)
    const customElementContainer = appendCustomElementToBody(node)

    this.mount()

    this.emit('update-nodes', { node })

    this.updateEmptyStatus()

    removeCustomElementFromBody(customElementContainer)

    const cartItem = this.element.querySelector<CartItem>(
      `cart-item[data-variant-id="${parsedState.id}"]`,
    )

    if (!cartItem) {
      return
    }

    if (parsedState.quantity === 1) {
      cartItem
        .collapse()
        .then(() => this.open())
        .then(() => cartItem.expand())
        .then(() => cartItem.showCheckmark())
    } else {
      this.open().then(() => cartItem.showCheckmark())
    }
  }

  getTemplateContent(parsedState) {
    const html = parseHTML(parsedState.sections[this.dataset.sectionId])
    const newTemplate = html.querySelector(
      `[data-sidebar-template="${this.id}"]`,
    )
    const node = getTemplateFirstChildContent(newTemplate)

    return node
  }

  updateEmptyStatus(): void {
    const cartItems = this.element.querySelectorAll('cart-item')
    const hasWarningPlaceholder = this.hasAttribute(
      'data-has-warning-placeholder',
    )

    if (hasWarningPlaceholder) {
      const isEmpty = cartItems.length === 0

      this.toggleAttribute('is-empty', isEmpty)
    }
  }

  createConfig(itemIndex: number, quantity: number): any {
    return {
      ...fetchConfig(),
      body: JSON.stringify({
        line: itemIndex + 1,
        quantity,
        sections: this.dataset.sectionId,
        sections_url: window.location.pathname,
      }),
    }
  }
}

export default CartDrawer
